import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Paper,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Slide,
  LinearProgress,
  Collapse
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Delete as DeleteIcon,
  Psychology as PsychologyIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MonitorHeart as VitalsIcon,
  MedicalServices as MedicalIcon,
  Person as PersonIcon,
  Healing as HealingIcon,
  Assignment as DiagnosisIcon,
  FamilyRestroom as FamilyIcon
} from '@mui/icons-material';

const API_URL = '/api';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Tarjeta de sección de datos extraídos
const SeccionDatos = ({ titulo, icon, color, children, checked, onToggle, vacia }) => {
  const [expandida, setExpandida] = useState(true);

  if (vacia) return null;

  return (
    <Paper sx={{
      mb: 1.5,
      borderRadius: 2,
      overflow: 'hidden',
      border: checked ? `1px solid ${color}40` : '1px solid #e2e8f0',
      opacity: checked ? 1 : 0.5,
      transition: 'all 0.2s'
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        bgcolor: checked ? `${color}08` : '#f8fafc',
        cursor: 'pointer'
      }}
        onClick={() => setExpandida(!expandida)}
      >
        <Checkbox
          checked={checked}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={{ p: 0.5, color, '&.Mui-checked': { color } }}
        />
        {React.cloneElement(icon, { sx: { fontSize: 18, color } })}
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f', flex: 1 }}>
          {titulo}
        </Typography>
        {expandida ? <ExpandLessIcon sx={{ fontSize: 18, color: '#94a3b8' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: '#94a3b8' }} />}
      </Box>
      <Collapse in={expandida}>
        <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0' }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

const ConsultaVoz = ({
  open,
  onClose,
  paciente,
  userId,
  expedienteId,
  onDatosExtraidos
}) => {
  // Fase: 'grabacion', 'procesando', 'resultados'
  const [fase, setFase] = useState('grabacion');

  // Grabación
  const [grabando, setGrabando] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [reproduciendo, setReproduciendo] = useState(false);
  const [tiempoGrabacion, setTiempoGrabacion] = useState(0);

  // Procesamiento
  const [pasoActual, setPasoActual] = useState('');
  const [progreso, setProgreso] = useState(0);

  // Resultados
  const [transcripcion, setTranscripcion] = useState('');
  const [datosExtraidos, setDatosExtraidos] = useState({});
  const [confianza, setConfianza] = useState(0);
  const [camposDetectados, setCamposDetectados] = useState([]);
  const [error, setError] = useState(null);

  // Secciones seleccionadas para enviar
  const [secciones, setSecciones] = useState({
    motivo: true,
    signos_vitales: true,
    exploracion: true,
    diagnosticos: true,
    tratamiento: true,
    antecedentes: true
  });

  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  // Limpiar al cerrar
  useEffect(() => {
    if (!open) {
      resetear();
    }
  }, [open]);

  // Timer de grabación
  useEffect(() => {
    if (grabando) {
      timerRef.current = setInterval(() => {
        setTiempoGrabacion(prev => prev + 1);
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [grabando]);

  const resetear = () => {
    setFase('grabacion');
    setGrabando(false);
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setReproduciendo(false);
    setTiempoGrabacion(0);
    setPasoActual('');
    setProgreso(0);
    setTranscripcion('');
    setDatosExtraidos({});
    setConfianza(0);
    setCamposDetectados([]);
    setError(null);
    setSecciones({
      motivo: true,
      signos_vitales: true,
      exploracion: true,
      diagnosticos: true,
      tratamiento: true,
      antecedentes: true
    });
  };

  const formatearTiempo = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const iniciarGrabacion = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(1000);
      setGrabando(true);
      setTiempoGrabacion(0);
      setError(null);
    } catch (err) {
      setError('No se pudo acceder al micrófono. Verifica los permisos del navegador.');
    }
  };

  const detenerGrabacion = () => {
    if (mediaRecorderRef.current && grabando) {
      mediaRecorderRef.current.stop();
      setGrabando(false);
    }
  };

  const toggleReproduccion = () => {
    if (audioRef.current) {
      if (reproduciendo) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setReproduciendo(!reproduciendo);
    }
  };

  const procesarAudio = async () => {
    if (!audioBlob) return;

    setFase('procesando');
    setError(null);
    setPasoActual('Transcribiendo audio con Whisper...');
    setProgreso(30);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'consulta-voz.webm');
      if (paciente) {
        const edad = calcularEdad(paciente.fecha_nacimiento);
        if (edad) formData.append('paciente_edad', edad);
        if (paciente.sexo) formData.append('paciente_sexo', paciente.sexo);
      }

      setPasoActual('Clasificando datos con IA...');
      setProgreso(60);

      const res = await fetch(`${API_URL}/emr/ia/consulta-voz`, {
        method: 'POST',
        headers: { 'X-User-Id': userId },
        body: formData
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || 'Error al procesar el audio');
      }

      setProgreso(90);
      setPasoActual('Organizando resultados...');

      const data = await res.json();
      setTranscripcion(data.transcripcion || '');
      setDatosExtraidos(data.datos_extraidos || {});
      setConfianza(data.confianza_general || 0);
      setCamposDetectados(data.campos_detectados || []);

      setProgreso(100);
      setFase('resultados');
    } catch (err) {
      setError(err.message);
      setFase('grabacion');
    }
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return null;
    const hoy = new Date();
    const nac = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nac.getFullYear();
    const m = hoy.getMonth() - nac.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
    return edad;
  };

  const handleUsarEnConsulta = () => {
    const datos = {};

    if (secciones.motivo) {
      if (datosExtraidos.motivo_consulta) datos.motivo_consulta = datosExtraidos.motivo_consulta;
      if (datosExtraidos.padecimiento_actual) datos.padecimiento_actual = datosExtraidos.padecimiento_actual;
    }

    if (secciones.signos_vitales && datosExtraidos.signos_vitales) {
      datos.signos_vitales = {};
      Object.entries(datosExtraidos.signos_vitales).forEach(([k, v]) => {
        if (v !== null && v !== undefined) datos.signos_vitales[k] = v;
      });
    }

    if (secciones.exploracion && datosExtraidos.exploracion_fisica) {
      datos.exploracion_fisica = datosExtraidos.exploracion_fisica;
    }

    if (secciones.diagnosticos && datosExtraidos.diagnosticos_sugeridos?.length) {
      datos.diagnosticos_sugeridos = datosExtraidos.diagnosticos_sugeridos;
    }

    if (secciones.tratamiento) {
      if (datosExtraidos.impresion_diagnostica) datos.impresion_diagnostica = datosExtraidos.impresion_diagnostica;
      if (datosExtraidos.plan_tratamiento) datos.plan_tratamiento = datosExtraidos.plan_tratamiento;
      if (datosExtraidos.indicaciones_paciente) datos.indicaciones_paciente = datosExtraidos.indicaciones_paciente;
    }

    if (secciones.antecedentes && datosExtraidos.antecedentes_detectados) {
      datos.antecedentes_detectados = datosExtraidos.antecedentes_detectados;
    }

    datos.transcripcion = transcripcion;

    if (onDatosExtraidos) {
      onDatosExtraidos(datos);
    }
    onClose();
  };

  // Helper: verificar si una sección tiene datos
  const tieneSignosVitales = datosExtraidos.signos_vitales &&
    Object.values(datosExtraidos.signos_vitales).some(v => v !== null && v !== undefined);

  const tieneExploracion = datosExtraidos.exploracion_fisica?.descripcion;
  const tieneDiagnosticos = datosExtraidos.diagnosticos_sugeridos?.length > 0;
  const tieneAntecedentes = datosExtraidos.antecedentes_detectados &&
    (datosExtraidos.antecedentes_detectados.heredo_familiares?.otros?.length > 0 ||
     datosExtraidos.antecedentes_detectados.patologicos?.enfermedades_previas?.length > 0 ||
     datosExtraidos.antecedentes_detectados.patologicos?.alergias?.length > 0 ||
     datosExtraidos.antecedentes_detectados.patologicos?.medicamentos_actuales?.length > 0 ||
     datosExtraidos.antecedentes_detectados.no_patologicos?.tabaquismo !== null ||
     datosExtraidos.antecedentes_detectados.no_patologicos?.alcoholismo !== null);

  const signosLabels = {
    presion_arterial_sistolica: { label: 'PA Sist.', unit: 'mmHg' },
    presion_arterial_diastolica: { label: 'PA Diast.', unit: 'mmHg' },
    frecuencia_cardiaca: { label: 'FC', unit: 'lpm' },
    frecuencia_respiratoria: { label: 'FR', unit: 'rpm' },
    temperatura: { label: 'Temp', unit: '°C' },
    peso: { label: 'Peso', unit: 'kg' },
    talla: { label: 'Talla', unit: 'cm' },
    saturacion_oxigeno: { label: 'SpO2', unit: '%' }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: '#f8fafc',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.15)',
          overflow: 'hidden',
          maxHeight: '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        color: '#fff',
        py: 2,
        px: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <MicIcon sx={{ fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>
              Consulta por Voz
            </Typography>
            {paciente && (
              <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
                {paciente.nombre_completo} - {calcularEdad(paciente.fecha_nacimiento)} a., {paciente.sexo}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {fase === 'resultados' && (
            <Chip
              label={`${Math.round(confianza * 100)}% confianza`}
              size="small"
              sx={{
                bgcolor: confianza > 0.7 ? 'rgba(255,255,255,0.25)' : 'rgba(239,68,68,0.3)',
                color: '#fff',
                fontWeight: 600,
                fontSize: 11
              }}
            />
          )}
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* FASE 1: Grabación */}
        {fase === 'grabacion' && (
          <Box>
            <Paper sx={{
              p: 4,
              textAlign: 'center',
              bgcolor: '#fff',
              borderRadius: 3,
              border: '1px solid #e2e8f0'
            }}>
              {/* Timer */}
              <Typography variant="h2" sx={{
                fontFamily: 'monospace',
                fontWeight: 700,
                mb: 2,
                color: grabando ? '#ef4444' : '#1e3a5f',
                letterSpacing: 4
              }}>
                {formatearTiempo(tiempoGrabacion)}
              </Typography>

              {grabando && (
                <LinearProgress
                  color="error"
                  sx={{ mb: 3, borderRadius: 2, height: 4 }}
                />
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
                {!grabando && !audioBlob && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<MicIcon />}
                    onClick={iniciarGrabacion}
                    sx={{
                      bgcolor: '#10b981',
                      borderRadius: '50px',
                      px: 5,
                      py: 1.5,
                      fontSize: 16,
                      fontWeight: 600,
                      boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                      '&:hover': {
                        bgcolor: '#059669',
                        boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)'
                      }
                    }}
                  >
                    Iniciar Grabaci&oacute;n
                  </Button>
                )}

                {grabando && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<StopIcon />}
                    onClick={detenerGrabacion}
                    sx={{
                      bgcolor: '#ef4444',
                      borderRadius: '50px',
                      px: 5,
                      py: 1.5,
                      fontSize: 16,
                      fontWeight: 600,
                      animation: 'pulse 1.5s ease-in-out infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.4)' },
                        '50%': { boxShadow: '0 0 0 12px rgba(239, 68, 68, 0)' }
                      },
                      '&:hover': { bgcolor: '#dc2626' }
                    }}
                  >
                    Detener
                  </Button>
                )}

                {audioBlob && !grabando && (
                  <>
                    <IconButton
                      onClick={toggleReproduccion}
                      sx={{
                        bgcolor: '#3b82f6',
                        color: '#fff',
                        width: 48,
                        height: 48,
                        '&:hover': { bgcolor: '#2563eb' }
                      }}
                    >
                      {reproduciendo ? <PauseIcon /> : <PlayIcon />}
                    </IconButton>
                    <IconButton
                      onClick={resetear}
                      sx={{
                        bgcolor: '#ef4444',
                        color: '#fff',
                        width: 48,
                        height: 48,
                        '&:hover': { bgcolor: '#dc2626' }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<PsychologyIcon />}
                      onClick={procesarAudio}
                      sx={{
                        bgcolor: '#10b981',
                        borderRadius: '50px',
                        px: 4,
                        py: 1.2,
                        fontWeight: 600,
                        boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                        '&:hover': {
                          bgcolor: '#059669',
                          boxShadow: '0 6px 20px rgba(16, 185, 129, 0.4)'
                        }
                      }}
                    >
                      Procesar con IA
                    </Button>
                  </>
                )}
              </Box>

              {!grabando && !audioBlob && (
                <Typography sx={{ color: '#94a3b8', fontSize: 13, mt: 2 }}>
                  Graba la conversaci&oacute;n con el paciente. La IA extraer&aacute; autom&aacute;ticamente los datos cl&iacute;nicos.
                </Typography>
              )}

              {audioUrl && (
                <audio
                  ref={audioRef}
                  src={audioUrl}
                  onEnded={() => setReproduciendo(false)}
                  style={{ display: 'none' }}
                />
              )}
            </Paper>
          </Box>
        )}

        {/* FASE 2: Procesando */}
        {fase === 'procesando' && (
          <Paper sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 3 }}>
            <CircularProgress
              size={64}
              sx={{ color: '#10b981', mb: 3 }}
            />
            <Typography sx={{ fontWeight: 600, fontSize: 18, color: '#1e3a5f', mb: 1 }}>
              {pasoActual}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progreso}
              sx={{
                mt: 2,
                height: 6,
                borderRadius: 3,
                bgcolor: '#e2e8f0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#10b981',
                  borderRadius: 3
                }
              }}
            />
            <Typography sx={{ color: '#94a3b8', fontSize: 12, mt: 1 }}>
              {progreso}%
            </Typography>
          </Paper>
        )}

        {/* FASE 3: Resultados */}
        {fase === 'resultados' && (
          <Box>
            {/* Transcripción */}
            <Paper sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f', mb: 1 }}>
                Transcripci&oacute;n
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={transcripcion}
                onChange={(e) => setTranscripcion(e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontSize: 13,
                    bgcolor: '#f8fafc'
                  }
                }}
              />
            </Paper>

            {/* Alert de IA */}
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography sx={{ fontSize: 12 }}>
                Datos extra&iacute;dos autom&aacute;ticamente por IA. Selecciona las secciones que deseas incluir en la consulta.
              </Typography>
            </Alert>

            {/* Secciones de datos */}

            {/* Motivo de consulta */}
            <SeccionDatos
              titulo="Motivo de Consulta"
              icon={<MedicalIcon />}
              color="#6366f1"
              checked={secciones.motivo}
              onToggle={() => setSecciones(p => ({ ...p, motivo: !p.motivo }))}
              vacia={!datosExtraidos.motivo_consulta && !datosExtraidos.padecimiento_actual}
            >
              {datosExtraidos.motivo_consulta && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Motivo</Typography>
                  <Typography sx={{ fontSize: 13, color: '#1e3a5f' }}>{datosExtraidos.motivo_consulta}</Typography>
                </Box>
              )}
              {datosExtraidos.padecimiento_actual && (
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Padecimiento actual</Typography>
                  <Typography sx={{ fontSize: 13, color: '#1e3a5f' }}>{datosExtraidos.padecimiento_actual}</Typography>
                </Box>
              )}
            </SeccionDatos>

            {/* Signos vitales */}
            <SeccionDatos
              titulo="Signos Vitales"
              icon={<VitalsIcon />}
              color="#ef4444"
              checked={secciones.signos_vitales}
              onToggle={() => setSecciones(p => ({ ...p, signos_vitales: !p.signos_vitales }))}
              vacia={!tieneSignosVitales}
            >
              <Grid container spacing={1}>
                {datosExtraidos.signos_vitales && Object.entries(datosExtraidos.signos_vitales).map(([key, val]) => {
                  if (val === null || val === undefined) return null;
                  const info = signosLabels[key] || { label: key, unit: '' };
                  return (
                    <Grid item xs={6} sm={3} key={key}>
                      <Box sx={{ p: 1, bgcolor: '#fef2f2', borderRadius: 1.5, textAlign: 'center' }}>
                        <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{info.label}</Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>
                          {val} <span style={{ fontSize: 10, fontWeight: 400 }}>{info.unit}</span>
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </SeccionDatos>

            {/* Exploración física */}
            <SeccionDatos
              titulo="Exploraci&oacute;n F&iacute;sica"
              icon={<PersonIcon />}
              color="#8b5cf6"
              checked={secciones.exploracion}
              onToggle={() => setSecciones(p => ({ ...p, exploracion: !p.exploracion }))}
              vacia={!tieneExploracion}
            >
              <Typography sx={{ fontSize: 13, color: '#1e3a5f' }}>
                {datosExtraidos.exploracion_fisica?.descripcion}
              </Typography>
            </SeccionDatos>

            {/* Diagnósticos sugeridos */}
            <SeccionDatos
              titulo={`Diagn&oacute;sticos Sugeridos (${datosExtraidos.diagnosticos_sugeridos?.length || 0})`}
              icon={<DiagnosisIcon />}
              color="#10b981"
              checked={secciones.diagnosticos}
              onToggle={() => setSecciones(p => ({ ...p, diagnosticos: !p.diagnosticos }))}
              vacia={!tieneDiagnosticos}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {datosExtraidos.diagnosticos_sugeridos?.map((d, i) => (
                  <Chip
                    key={i}
                    label={`${d.codigo_cie10 || '?'}: ${d.descripcion}`}
                    size="small"
                    sx={{
                      bgcolor: d.probabilidad === 'alta' ? '#dcfce7' : d.probabilidad === 'media' ? '#fef9c3' : '#f1f5f9',
                      color: d.probabilidad === 'alta' ? '#166534' : d.probabilidad === 'media' ? '#854d0e' : '#475569',
                      fontSize: 11,
                      fontWeight: 500
                    }}
                  />
                ))}
              </Box>
            </SeccionDatos>

            {/* Tratamiento e indicaciones */}
            <SeccionDatos
              titulo="Tratamiento e Indicaciones"
              icon={<HealingIcon />}
              color="#f59e0b"
              checked={secciones.tratamiento}
              onToggle={() => setSecciones(p => ({ ...p, tratamiento: !p.tratamiento }))}
              vacia={!datosExtraidos.impresion_diagnostica && !datosExtraidos.plan_tratamiento && !datosExtraidos.indicaciones_paciente}
            >
              {datosExtraidos.impresion_diagnostica && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Impresi&oacute;n diagn&oacute;stica</Typography>
                  <Typography sx={{ fontSize: 13, color: '#1e3a5f' }}>{datosExtraidos.impresion_diagnostica}</Typography>
                </Box>
              )}
              {datosExtraidos.plan_tratamiento && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Plan de tratamiento</Typography>
                  <Typography sx={{ fontSize: 13, color: '#1e3a5f' }}>{datosExtraidos.plan_tratamiento}</Typography>
                </Box>
              )}
              {datosExtraidos.indicaciones_paciente && (
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b', mb: 0.5 }}>Indicaciones al paciente</Typography>
                  <Typography sx={{ fontSize: 13, color: '#1e3a5f' }}>{datosExtraidos.indicaciones_paciente}</Typography>
                </Box>
              )}
            </SeccionDatos>

            {/* Antecedentes detectados */}
            <SeccionDatos
              titulo="Antecedentes Detectados"
              icon={<FamilyIcon />}
              color="#0ea5e9"
              checked={secciones.antecedentes}
              onToggle={() => setSecciones(p => ({ ...p, antecedentes: !p.antecedentes }))}
              vacia={!tieneAntecedentes}
            >
              {datosExtraidos.antecedentes_detectados?.patologicos?.alergias?.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Alergias</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {datosExtraidos.antecedentes_detectados.patologicos.alergias.map((a, i) => (
                      <Chip key={i} label={a} size="small" sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontSize: 11 }} />
                    ))}
                  </Box>
                </Box>
              )}
              {datosExtraidos.antecedentes_detectados?.patologicos?.enfermedades_previas?.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Enfermedades previas</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {datosExtraidos.antecedentes_detectados.patologicos.enfermedades_previas.map((e, i) => (
                      <Chip key={i} label={e} size="small" sx={{ bgcolor: '#f1f5f9', fontSize: 11 }} />
                    ))}
                  </Box>
                </Box>
              )}
              {datosExtraidos.antecedentes_detectados?.patologicos?.medicamentos_actuales?.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>Medicamentos actuales</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {datosExtraidos.antecedentes_detectados.patologicos.medicamentos_actuales.map((m, i) => (
                      <Chip key={i} label={m} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: 11 }} />
                    ))}
                  </Box>
                </Box>
              )}
            </SeccionDatos>
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
        <Button onClick={onClose} color="inherit" sx={{ borderRadius: 2, fontWeight: 600 }}>
          Cancelar
        </Button>
        {fase === 'resultados' && (
          <>
            <Button
              variant="outlined"
              onClick={resetear}
              startIcon={<MicIcon />}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                borderColor: '#e2e8f0',
                color: '#64748b',
                '&:hover': { background: '#f1f5f9', borderColor: '#94a3b8' }
              }}
            >
              Grabar de Nuevo
            </Button>
            <Button
              variant="contained"
              onClick={handleUsarEnConsulta}
              startIcon={<SendIcon />}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                px: 3,
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)'
                }
              }}
            >
              Usar en Consulta
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConsultaVoz;
